import { Component, ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            padding: 'var(--spacing-xl)',
            textAlign: 'center',
          }}
        >
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--color-error)"
            strokeWidth="2"
            style={{ marginBottom: 'var(--spacing-lg)' }}
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <h2 style={{ marginBottom: 'var(--spacing-sm)' }}>Something went wrong</h2>
          <p
            style={{
              color: 'var(--color-text-secondary)',
              marginBottom: 'var(--spacing-lg)',
              maxWidth: '500px',
            }}
          >
            An unexpected error occurred. Please try again.
          </p>
          {this.state.error && (
            <pre
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--spacing-lg)',
                maxWidth: '600px',
                overflow: 'auto',
                padding: 'var(--spacing-md)',
                backgroundColor: 'var(--color-surface, var(--color-bg-secondary))',
                borderRadius: 'var(--radius-md)',
                textAlign: 'left',
              }}
            >
              {this.state.error.message}
            </pre>
          )}
          <div style={{ display: 'flex', gap: 'var(--spacing-md)' }}>
            <button className="btn btn-ghost" onClick={this.handleReset}>
              Try Again
            </button>
            <button className="btn btn-primary" onClick={() => window.location.assign('/dashboard')}>
              Go to Dashboard
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
